import {
  GrowthBookProvider,
  useFeatureIsOn,
} from '@growthbook/growthbook-react'
import { type ReactNode, useEffect } from 'react'

import {
  SKIP_STYLE_ADVISOR_SELECTION_FLAG,
  type Environment,
} from '../../config'
import Avo from '../../../Avo/Avo'
import { flowProperties } from '../analytics'
import { useBookingStore } from '../../store'
import { getGrowthBook, getVisitorId, loadFeatures } from './growthbook'

/** What to report to Avo when GrowthBook records an experiment exposure. */
export interface ExperimentViewedPayload {
  experimentId: string
  variationId: string
  /** The flow's store (`state.locationCode`), '' before one is known — same value the package's own events send. */
  locationCode: string
}

interface FeatureFlagsProviderProps {
  env: Environment
  children: ReactNode
  /**
   * Called once per visitor when GrowthBook buckets them into a real
   * Experiment, AND once per visitor when a plain targeting rule (a
   * `force` rule with a matching condition, no experiment attached) fires
   * for a flag this provider explicitly tracks that way — see
   * `setFeatureUsageCallback` below for why a plain rule needs its own
   * path. Never twice for the same visitor+value (the SDK dedupes the
   * experiment case on its own; the feature-usage case is deduped the same
   * way by GrowthBook internally).
   *
   * When passed, the host owns the exposure: the package does not send its
   * own Avo `Experiment Viewed` as well (see `reportExposure`).
   */
  onExperimentViewed?: (payload: ExperimentViewedPayload) => void
}

/**
 * One exposure, one sink. A host that passes `onExperimentViewed` already
 * forwards it to its own `Experiment Viewed` (storefront, rp-booking-tool), so
 * sending the package's too would count every exposure twice. Without one, the
 * package sends its own Avo `Experiment Viewed` (DA-1554 — carries `flow_id`
 * and the booking bundle).
 *
 * Sent from here rather than from a `trackingCallback` in `getGrowthBook`'s
 * constructor: `setTrackingCallback` below replaces that option, and the
 * force-rule pilot only ever reaches `setFeatureUsageCallback` anyway.
 */
function reportExposure(
  exposure: Omit<ExperimentViewedPayload, 'locationCode'>,
  onExperimentViewed?: (payload: ExperimentViewedPayload) => void,
): void {
  if (!onExperimentViewed) {
    Avo.experimentViewed({
      ...flowProperties(),
      eventCategory: null,
      ...exposure,
    })
    return
  }
  onExperimentViewed({
    ...exposure,
    locationCode: useBookingStore.getState().locationCode ?? '',
  })
}

/**
 * Makes GrowthBook flags readable from any screen. Mounted by
 * `BookingToolRoot` around the whole flow, and deliberately not blocking on
 * the payload: only a screen a flag can step over waits for it, so a slow CDN
 * cannot hold up the booking flow.
 */
export const FeatureFlagsProvider = ({
  env,
  children,
  onExperimentViewed,
}: FeatureFlagsProviderProps) => {
  const growthbook = getGrowthBook(env)
  const locationCode = useBookingStore((state) => state.locationCode)

  useEffect(() => {
    void loadFeatures(env)
  }, [env])

  // The attributes the flag rules target, refreshed as the flow learns them.
  // This is the only writer, so the whole attribute set is written outright —
  // add a spread here if that ever stops being true (a plugin, a second
  // writer). `id` is the hash attribute a percentage-split rule needs to
  // randomize per visitor rather than per store.
  useEffect(() => {
    void growthbook.setAttributes({
      id: getVisitorId(),
      locationCode: locationCode ?? null,
    })
  }, [growthbook, locationCode])

  // Registered even without a host callback so the SDK's own dedup state
  // isn't tied to whether one is attached; `onExperimentViewed` is checked at
  // call time (a ref-free closure is fine here, `setTrackingCallback` just
  // replaces the previous one — cheap enough to redo whenever the prop changes).
  //
  // `result.key` — GrowthBook's own variation key — rather than reading
  // `result.value` as this one flag's boolean: this callback is registered
  // for the whole client, so it has to report whatever experiment GrowthBook
  // hands it, not just this pilot's skip/show pair.
  useEffect(() => {
    growthbook.setTrackingCallback((experiment, result) => {
      if (!result.inExperiment) return

      reportExposure(
        { experimentId: experiment.key, variationId: result.key },
        onExperimentViewed,
      )
    })
  }, [growthbook, onExperimentViewed])

  // `RP-skip-styleadvisor-selection` is a plain GrowthBook targeting rule
  // (`force: true` on a `locationCode` condition) rather than a real
  // Experiment — reconfiguring it as one is a live-feature change we're
  // deliberately not making, so `trackingCallback` above (GrowthBook's own
  // `inExperiment`-gated exposure hook) never fires for it, no matter where
  // it's evaluated. `setFeatureUsageCallback` fires for every feature read
  // instead, force rules included, and GrowthBook already dedupes it per
  // visitor (only refires if the evaluated value changes) — report a match
  // through the same `onExperimentViewed` payload a real experiment would
  // use, so a host's analytics doesn't need to know the difference.
  useEffect(() => {
    growthbook.setFeatureUsageCallback((key, result) => {
      if (key !== SKIP_STYLE_ADVISOR_SELECTION_FLAG) return
      // Only a rule that actually matched counts as "viewed" — a visitor
      // outside the targeting condition falling through to `defaultValue`
      // was never exposed to anything.
      if (result.source === 'defaultValue') return

      reportExposure(
        { experimentId: key, variationId: String(result.value) },
        onExperimentViewed,
      )
    })
  }, [growthbook, onExperimentViewed])

  return (
    <GrowthBookProvider growthbook={growthbook}>
      <ExperimentExposure />
      {children}
    </GrowthBookProvider>
  )
}

/**
 * Evaluates `SKIP_STYLE_ADVISOR_SELECTION_FLAG` unconditionally, purely so
 * GrowthBook actually reads the flag — and so `setFeatureUsageCallback`
 * above has something to fire on — regardless of which screen the flow
 * lands on.
 *
 * `useStyleAdvisorScreen` only runs where `EmployeesList` mounts, and some
 * deep-links (`?employee-id=…`, reschedule's `?booking-id=…`) land straight
 * on `timeslots` without ever mounting it — so on those routes the flag was
 * never evaluated and "Experiment Viewed" never fired even though the
 * visitor's `locationCode` matched the targeting rule. Reading the flag
 * here, once per mount regardless of screen, closes that gap. The boolean
 * itself is discarded — screens still call `useStyleAdvisorScreen` for the
 * behaviour; this component exists only for the exposure side effect.
 */
const ExperimentExposure = () => {
  useFeatureIsOn(SKIP_STYLE_ADVISOR_SELECTION_FLAG)
  return null
}
